import React, { useContext, useEffect, useRef, useState } from "react";

import { AuthContext } from "../../context/AuthContext";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { IPortfolioItem } from "../../mock-data/portfolioData";
import { IZeosUser } from "../../services/auth/IZeosUser";
import { PortfolioModalType } from "../../services/helpers/PortfolioHelpers";
import { handleOpenLinkUtilityAsync } from "../../services/helpers/UtilityLinkHelpers";
import { UapiStatus } from "../../services/zeos-api/enums/Uapi";
import { invalidateDashboardOverviewCache } from "../Dashboard/MainDashboard";
import { invalidateNextGenSeriesCache } from "../NextGenDashboard/NextGenDashboard";
import { ZeosSpinner } from "../Shared/ZeosSpinner";
import { UtilityLinkSelector } from "../utility-link/UtilityLinkSelector";

// MTR-P1-08 add-a-meter (task-4061, spec v0.3 R8). Runs the normal UtilityAPI Link Utility form again for a
// property that already has meters: the new authorization's meters are synced onto the same property (task-4055)
// and land as Unassigned, next to the existing ones. UtilityAPI only: a second Bayou link replaces the property's
// Bayou meters rather than adding to them. Same NY / NJ gate as Link Utility.

interface IProps {
  propertyId: number;
  propertyName?: string;
  /** linked: the authorization completed (its meters may take a few minutes to appear). */
  onFinished: (linked: boolean) => void;
}

export const LinkAnotherMeter = ({ propertyId, propertyName, onFinished }: IProps) => {
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  // A ref, not state: the selector reports LINKED and then closes in the same tick.
  const linkedRef = useRef(false);

  const property = { propertyId, name: propertyName ?? "" } as IPortfolioItem;

  useEffect(() => {
    let cancelled = false;
    const checkAsync = async () => {
      const [user, linkType] = await Promise.all([
        auth.zeosUserAsync(),
        handleOpenLinkUtilityAsync(property, api),
      ]);
      if (cancelled) return;
      setZeosUser(user);
      if (linkType === PortfolioModalType.None) {
        // handleOpenLinkUtilityAsync has already told the user why.
        onFinished(false);
        return;
      }
      setAllowed(true);
    };
    checkAsync();
    return () => {
      cancelled = true;
    };
    // Checked once when the flow opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const handleStatusChange = (status: UapiStatus) => {
    if (status !== UapiStatus.LINKED) return;
    linkedRef.current = true;
    invalidateDashboardOverviewCache(propertyId);
    invalidateNextGenSeriesCache(propertyId);
  };

  if (allowed === null) {
    return <ZeosSpinner message="Checking utility linking…" />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-lg font-semibold text-black">Link another meter</h3>
        <p className="text-sm text-gray-600">
          Authorize another utility account for {propertyName || "this property"}. Its meters are added to this
          property alongside the ones already linked, as Unassigned until someone classifies them. They can take a few
          minutes to appear.
        </p>
      </div>
      <UtilityLinkSelector
        property={property}
        zeosUser={zeosUser}
        uapiOnly
        onStatusChange={handleStatusChange}
        onClose={() => onFinished(linkedRef.current)}
      />
    </div>
  );
};
